import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { MarketingWebsiteSeoConfig } from './marketing-website-seo.models';
import {
  buildMarketingCanonicalUrl,
  buildMarketingDocumentTitle,
  buildMarketingPageJsonLd,
  buildMarketingShareImageUrl,
  resolveMarketingSiteOrigin,
} from './marketing-website-seo.utils';

const JSON_LD_SCRIPT_ID = 'milinco-marketing-jsonld';
const CANONICAL_LINK_ID = 'milinco-marketing-canonical-link';
const MARKETING_HTML_LANG = 'es';

@Injectable({ providedIn: 'root' })
export class MarketingWebsiteSeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly doc = inject(DOCUMENT);

  apply(config: MarketingWebsiteSeoConfig): void {
    const origin =
      resolveMarketingSiteOrigin(config.origin) ||
      (typeof window !== 'undefined' ? window.location.origin : '');
    const documentTitle = buildMarketingDocumentTitle(config.documentTitle);
    const description = config.metaDescription.trim();
    const canonicalUrl = buildMarketingCanonicalUrl(origin, config.path);
    const shareImage = buildMarketingShareImageUrl(origin);

    this.title.setTitle(documentTitle);
    this.doc.documentElement.lang = MARKETING_HTML_LANG;

    this.upsertMetaName('description', description);
    this.upsertMetaName('robots', 'index,follow');

    this.upsertMetaProperty('og:title', documentTitle);
    this.upsertMetaProperty('og:description', description);
    this.upsertMetaProperty('og:url', canonicalUrl);
    this.upsertMetaProperty('og:type', 'website');
    this.upsertMetaProperty('og:site_name', 'Milinco');
    this.upsertMetaProperty('og:locale', 'es_CO');
    this.upsertMetaProperty('og:image', shareImage);

    this.upsertMetaName('twitter:card', 'summary_large_image');
    this.upsertMetaName('twitter:title', documentTitle);
    this.upsertMetaName('twitter:description', description);
    this.upsertMetaName('twitter:image', shareImage);

    this.upsertCanonicalLink(canonicalUrl);
    this.upsertJsonLd(buildMarketingPageJsonLd({ ...config, origin }));
  }

  clear(): void {
    this.title.setTitle('Milinco');
    this.doc.documentElement.lang = MARKETING_HTML_LANG;

    this.removeMetaName('description');
    this.removeMetaName('robots');
    this.removeMetaProperty('og:title');
    this.removeMetaProperty('og:description');
    this.removeMetaProperty('og:url');
    this.removeMetaProperty('og:type');
    this.removeMetaProperty('og:site_name');
    this.removeMetaProperty('og:locale');
    this.removeMetaProperty('og:image');
    this.removeMetaName('twitter:card');
    this.removeMetaName('twitter:title');
    this.removeMetaName('twitter:description');
    this.removeMetaName('twitter:image');

    this.removeCanonicalLink();
    this.removeJsonLd();
  }

  private upsertMetaName(name: string, content: string): void {
    this.meta.updateTag({ name, content });
  }

  private upsertMetaProperty(property: string, content: string): void {
    this.meta.updateTag({ property, content });
  }

  private removeMetaName(name: string): void {
    this.meta.removeTag(`name='${name}'`);
  }

  private removeMetaProperty(property: string): void {
    this.meta.removeTag(`property='${property}'`);
  }

  private upsertCanonicalLink(href: string): void {
    const head = this.doc.head;
    if (!head) {
      return;
    }

    let link = this.doc.getElementById(CANONICAL_LINK_ID) as HTMLLinkElement | null;
    if (!link) {
      link = this.doc.createElement('link');
      link.id = CANONICAL_LINK_ID;
      link.rel = 'canonical';
      head.appendChild(link);
    }
    link.href = href;
  }

  private removeCanonicalLink(): void {
    this.doc.getElementById(CANONICAL_LINK_ID)?.remove();
  }

  private upsertJsonLd(json: string): void {
    const head = this.doc.head;
    if (!head) {
      return;
    }

    let script = this.doc.getElementById(JSON_LD_SCRIPT_ID) as HTMLScriptElement | null;
    if (!script) {
      script = this.doc.createElement('script');
      script.id = JSON_LD_SCRIPT_ID;
      script.type = 'application/ld+json';
      head.appendChild(script);
    }
    script.textContent = json;
  }

  private removeJsonLd(): void {
    this.doc.getElementById(JSON_LD_SCRIPT_ID)?.remove();
  }
}
